import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Checkbox, DatePicker, Dropdown, Menu, message, Modal, Select, Table } from 'antd';
import { Search, ArrowLeft, Send, Eye, MessageSquareReply, AlertCircle, 
         Phone, Mail, MessageCircle, Calendar, CheckCircle2, Download, 
         X, ArrowUpRight,
         MessageSquare,
         Pause,
         Play} from 'lucide-react';
import debounce from 'lodash/debounce';
import  GenericConfirmationModal from '../../components/GenericConfirmationModal';
import { getCampaignById, updateCampaign } from '../../services/broadcast';
import type { CampaignViewProps, CampaignFormData, LeadData, StatusType, ErrorObject, ContentStructure } from './campaignViewTypes';
import { STATUS_COLORS } from './campaignViewTypes';
import type { MenuProps } from 'antd';
import './campaignView.css';
import LeadExportService from '../../services/LeadExportService';
import moment from 'moment';
import ContactLinksGenerator from '../../utils/contactlinksgenerator';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/userService';
import LeadDetails from '../leads/leadDetails/leadDetails';
import DrawerComponent from '../../components/drawer';
import { useNavigate } from 'react-router-dom';


const getErrorDisplay = (error: ErrorObject | ErrorObject[] | string | null | undefined): string | null => {
  if (!error) return null;

  // Case 1: If error is a string
  if (typeof error === 'string') {
    try {
      // Try to parse the string as JSON
      const parsed = JSON.parse(error);
      return getErrorDisplay(parsed); // Recursively handle parsed object/array
    } catch {
      // If parsing fails, return the string as-is
      return error;
    }
  }

  // Case 2: If error is an array
  if (Array.isArray(error)) {
    const errorMessages = error
      .map((e) => getErrorDisplay(e)) // Recursively process each element
      .filter(Boolean); // Remove empty/null values
    return errorMessages.length > 0 ? errorMessages.join(', ') : 'Error occurred';
  }

  // Case 3: If error is an object
  if (typeof error === 'object' && error !== null) {
    // Extract error message from possible properties
    return (
      error.error_data?.details ?? // Check error_data.details first
      error.message ?? // Fallback to message
      error.title ?? // Fallback to title
      'Error occurred' // Default fallback
    );
  }

  // Case 4: If error is of an unexpected type
  return 'Error occurred';
};

const renderTemplateOrContent = (campaignData: CampaignFormData) => {
  if (!campaignData) return null;

  const serviceType = campaignData.serviceType?.toLowerCase() ?? '';
  
  // Handle WhatsApp templates
  if (serviceType === 'whatsapp' && campaignData.whatsappTemplate?.name) {
    return (
      <>
        <span>•</span>
        <span>Template: {campaignData.whatsappTemplate.name}</span>
      </>
    );
  }

  // Handle Content
  if (campaignData.content) {
    // If content is a string, render it directly
    if (typeof campaignData.content === 'string') {
      return (
        <>
          <span>•</span>
          <span>Content: {campaignData.content}</span>
        </>
      );
    }

    // If content is our structured object
    if (typeof campaignData.content === 'object' && 'details' in campaignData.content) {
      const { details } = campaignData.content as unknown as ContentStructure;
      const displayText = details.title || details.subject || 'Untitled';
      
      return (
        <>
          <span>•</span>
          <span>Content: {displayText}</span>
        </>
      );
    }
  }

  return null;
};

const LoadingSpinner: React.FC = () => (
  <div className="loading-spinner">Loading...</div>
);

const CampaignView: React.FC<CampaignViewProps> = ({ campaignId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [campaignData, setCampaignData] = useState<CampaignFormData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState<StatusType | ''>('');
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [formCloseModel, setFormCloseModel] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
const [showRescheduleModal, setShowRescheduleModal] = useState(false);
const [newScheduleDate, setNewScheduleDate] = useState<Date | null>(null);

// Add these state declarations
const [confirmCancelModal, setConfirmCancelModal] = useState<boolean>(false);
const [confirmPauseModal, setConfirmPauseModal] = useState<boolean>(false);

  const [openWithHeader, setOpenWithHeader] = useState(false);
const [selectedLead, setSelectedLead] = useState<any>(null);

const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
const [allLeadsSelected, setAllLeadsSelected] = useState(false);
const navigate = useNavigate();


const onLeadClick = (lead: any) => {
  if (lead?._id) {
    setSelectedLead(lead);
    setOpenWithHeader(true);
  } else {
    message.error('Invalid lead selected');
  }
};

  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getCampaignById(campaignId, {
        page: currentPage,
        perPage: pageSize,
        status: selectedStatus || undefined,
        search: searchTerm || undefined,
        orderBy: 'lastActivity',
        isAscending: false
      });

      setCampaignData(data.data);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    } finally {
      setLoading(false);
    }
  }, [campaignId, currentPage, pageSize, selectedStatus, searchTerm]);

  useEffect(() => {
    fetchCampaignData();
  }, [fetchCampaignData]);

  // Debounced search handler
  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 300),
    []
  );

// Update the handleSelectAllLeads function with proper null checking
const handleSelectAllLeads = async (checked: boolean) => {
  if (checked) {
    // If we're selecting all, we need to get all campaign leads, not just the current page
    setAllLeadsSelected(true);
    
    try {
      // Make sure campaignData is not null before accessing properties
      if (!campaignData) {
        message.error('Campaign data not available');
        return;
      }
      
      // We need to get all leads for this campaign, not just the current page
      const { data } = await getCampaignById(campaignId, {
        page: 1,
        perPage: campaignData.totalLeads || 1000, // Fallback to a large number if totalLeads is undefined
        status: selectedStatus || undefined,
        search: searchTerm || undefined,
        orderBy: 'lastActivity',
        isAscending: false
      });
      
      if (data?.data?.leads) {
        const allLeadIds = data.data.leads.map((lead: any) => lead._id);
        setSelectedLeads(allLeadIds);
        message.success(`Selected all ${allLeadIds.length} leads`);
      }
    } catch (error) {
      console.error('Error fetching all leads:', error);
      message.error('Failed to select all leads');
      setAllLeadsSelected(false);
    }
  } else {
    // If unchecking, clear all selections
    setSelectedLeads([]);
    setAllLeadsSelected(false);
  }
};
  
const CampaignActions: React.FC<{
  status: string;
  isPaused?: boolean;
  onPause: () => void;
  onCancel: () => void;
  onReschedule: () => void;
}> = ({ status, isPaused, onPause, onCancel, onReschedule }) => {
  // Determine which actions are available based on status
  const canPause = ['pending', 'approved', 'started', 'in_progress', 'started_sending', 'scheduled'].includes(status.toLowerCase());
  const canCancel = ['pending', 'approved', 'started', 'in_progress', 'paused', 'started_sending', 'scheduled'].includes(status.toLowerCase());
  const canReschedule = ['pending', 'approved', 'paused'].includes(status.toLowerCase());

  return (
    <div className="campaign-actions">
      {canPause && (
        <Button 
          onClick={onPause}
          className={isPaused ? "resume-btn" : "pause-btn"}
        >
          {isPaused ? (
            <>
              <Play size={16} />&nbsp;Resume
            </>
          ) : (
            <>
              <Pause size={16} />&nbsp;Pause
            </>
          )}
        </Button>
      )}
      
      {canCancel && (
        <Button onClick={onCancel} className="cancel-btn">
          <X size={16} />&nbsp;Cancel
        </Button>
      )}
      
      {canReschedule && (
        <Button onClick={onReschedule} className="reschedule-btn">
          <Calendar size={16} />&nbsp;Reschedule
        </Button>
      )}
    </div>
  );
};

  // Add this function to create a new campaign with selected leads
  const handleCreateCampaign = () => {
    if (selectedLeads.length > 0) {
      // Store selected lead IDs in localStorage
      localStorage.setItem('selectedCampaignLeads', JSON.stringify(selectedLeads));
      message.success(`Creating campaign with ${selectedLeads.length} leads`);
      navigate('/createWhatsappBroadcast');
    }
  };


  // Add this with other handlers in CampaignView component
const handleStatusChange = (value: StatusType | '') => {
  setSelectedStatus(value);
  setCurrentPage(1); // Reset to first page when status changes
};

// Add immediate input handler
const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const value = e.target.value;
  setInputValue(value);  // Update UI immediately
  handleSearch(value);   // Debounced API call
};

const handleExportModalToggle = () => {
  setIsExportModalOpen(!isExportModalOpen);
};

// Modify clear handler to clear both states
const handleClearSearch = () => {
  setInputValue('');
  setSearchTerm('');
  setCurrentPage(1);
};


const { userDetails } = useSelector((state: any) => {
  return state?.rootReducers.userData;
});

// Handler functions
const handlePauseToggle = async () => {
  try {
    setActionLoading('pause');
    await updateCampaign(campaignId, { 
      isPaused: !campaignData?.isPaused 
    });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success(campaignData?.isPaused ? 'Campaign resumed' : 'Campaign paused');
    setConfirmPauseModal(false); // Add this line
  } catch (error) {
    console.error('Error toggling pause state:', error);
    message.error('Failed to update campaign status');
  } finally {
    setActionLoading(null);
  }
};

const handleCancel = async () => {
  try {
    setActionLoading('cancel');
    await updateCampaign(campaignId, { isCancelled: true });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success('Campaign cancelled');
    setConfirmCancelModal(false); // Add this line
  } catch (error) {
    console.error('Error cancelling campaign:', error);
    message.error('Failed to cancel campaign');
  } finally {
    setActionLoading(null);
  }
};

const handleReschedule = async () => {
  if (!newScheduleDate) {
    message.error('Please select a new date');
    return;
  }
  
  try {
    setActionLoading('reschedule');
    await updateCampaign(campaignId, { 
      scheduleAt: newScheduleDate.toISOString() 
    });
    
    // Refresh campaign data
    fetchCampaignData();
    message.success('Campaign rescheduled');
    setShowRescheduleModal(false);
  } catch (error) {
    console.error('Error rescheduling campaign:', error);
    message.error('Failed to reschedule campaign');
  } finally {
    setActionLoading(null);
  }
};



const confirmExport = async () => {
  try {
    await LeadExportService.exportLeads(
      campaignId,
      {
        status: selectedStatus,
        searchTerm: searchTerm
      }
    );
    
    setIsExportModalOpen(false);
  } catch (error) {
    console.error('Export error:', error);
    setIsExportModalOpen(false);
  }
};

// Helper function to determine if the service is an email type
const isEmailService = (serviceType: string | undefined): boolean => {
  if (!serviceType) return false;
  const type = serviceType.toLowerCase();
  return type === 'zeptomail' || type === 'gmail' || type === 'brevo' || type === 'email';
};


  // Keeping the styling components
  const StatusBadge: React.FC<{ status: StatusType }> = ({ status }) => (
    <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[status] }}>
      {status}
    </span>
  );

  const ActionButtons: React.FC<{ lead: LeadData }> = ({ lead }) => {
    const handleAction = (url: string) => {
      window.open(url, '_blank');
    };
  
    const contactLinks = new ContactLinksGenerator({
      number: lead.phone || '',
      email: lead.email || '',
      message: `Hi ${lead.name || ''},`
    });
  
    return (
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2">
        {lead.phone && (
          <button 
            onClick={() => handleAction(contactLinks.tel)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Call"
          >
            <Phone size={16} strokeWidth={2} className="text-[#1677FF]" />
          </button>
        )}
        
        {lead.email && (
          <button 
            onClick={() => handleAction(contactLinks.mail)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Email"
          >
            <Mail size={16} strokeWidth={2} className="text-[#F1B003]" />
          </button>
        )}
        
        {lead.phone && (
          <button 
            onClick={() => handleAction(contactLinks.whatsapp)}
            className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="WhatsApp"
          >
            <MessageCircle size={16} strokeWidth={2} className="text-[#4CB050]" />
          </button>
        )}

        <button 
          onClick={(e) => {
            e.stopPropagation();
            onLeadClick(lead);
          }}
          className="p-2.5 hover:bg-gray-100 rounded-full transition-colors duration-200"
          title="View Lead Details"
        >
          <ArrowUpRight size={16} strokeWidth={2} className="text-gray-600" />
        </button>
      </div>
    );
};

  if (!campaignData) {
    return <LoadingSpinner />;
  }
  


 

 return (
  <div className="campaign-view">
<div className="campaign-header">
  <div className="header-content">
    <button className="back-button" onClick={onClose}>
      <ArrowLeft size={24} />
    </button>
    <div className="campaign-info">
      <div className="campaign-title-row">
        <h1>{campaignData.name}</h1>
        {/* Add the campaign status badge here */}
        <span 
          className={`status-badge ${campaignData.status.toLowerCase()}`}
          style={{ 
            backgroundColor: STATUS_COLORS[campaignData.status.toLowerCase() as StatusType] || '#8c8c8c',
            marginLeft: '12px',
            fontSize: '13px',
            padding: '4px 12px'
          }}
        >
          {campaignData.status.charAt(0).toUpperCase() + campaignData.status.slice(1)}
        </span>
      </div>
      <div className="campaign-meta">
        <span>
          Sent by {campaignData.createdBy.firstName} {campaignData.createdBy.lastName}
        </span>
        <span>•</span>
        <span><Calendar size={14} className="inline mr-1" /> Scheduled for: {moment(campaignData.scheduleAt).format('Do MMM YYYY [at] h:mm A')}</span>
        {renderTemplateOrContent(campaignData)}
      </div>
      {campaignData.error && getErrorDisplay(campaignData.error) && (
        <div className="error-message text-red-500 mt-1 text-sm">
          {getErrorDisplay(campaignData.error)}
        </div>
      )}
    </div>
    {/* The info message and action buttons remain unchanged */}
    <div className="info-message">
  <AlertCircle size={16} color="#faad14" className="flex-shrink-0 mr-2" />
  <span className="text-sm">Metrics may update as more data is received</span>
</div>
    <div className="header-actions">
      <CampaignActions
        status={campaignData.status}
        isPaused={campaignData.isPaused}
        onPause={handlePauseToggle}
        onCancel={() => setConfirmCancelModal(true)}
        onReschedule={() => setShowRescheduleModal(true)}
      />
    </div>
  </div>
</div>
   




{/* Cancel Confirmation Modal */}
<GenericConfirmationModal
  showModal={confirmCancelModal}
  toggleModal={() => setConfirmCancelModal(false)}
  onConfirmation={handleCancel}
  title="Cancel Campaign"
  message="Are you sure you want to cancel this campaign? This action cannot be undone."
  confirmText="Cancel Campaign"
  cancelText="Keep Campaign Active"
  isWarning={true}
/>

<Modal
  title="Reschedule Campaign"
  visible={showRescheduleModal}
  onCancel={() => setShowRescheduleModal(false)}
  onOk={handleReschedule}
  confirmLoading={actionLoading === 'reschedule'}
>
  <div className="reschedule-form">
    <div className="form-group">
      <label>New Schedule Date and Time:</label>
      <DatePicker
        showTime
        format="YYYY-MM-DD HH:mm"
        onChange={(date) => setNewScheduleDate(date as any)}
        disabledDate={(date) => date.isBefore(moment(), 'day')}
        className="full-width"
      />
    </div>
  </div>
</Modal>
{/* Pause/Resume Confirmation Modal */}
<GenericConfirmationModal
  showModal={confirmPauseModal}
  toggleModal={() => setConfirmPauseModal(false)}
  onConfirmation={handlePauseToggle}
  title={campaignData?.isPaused ? "Resume Campaign" : "Pause Campaign"}
  message={campaignData?.isPaused 
    ? "Are you sure you want to resume this campaign? Messages will continue to be sent." 
    : "Are you sure you want to pause this campaign? No messages will be sent until resumed."}
  confirmText={campaignData?.isPaused ? "Resume" : "Pause"}
  cancelText="Cancel"
  isWarning={false}
/>

    <div className="campaign-content">
     <div className="metrics-grid">
        {isEmailService(campaignData.serviceType) ? (
          // Email Campaign Metrics
          <>
            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Queued</span>
                  <Send size={18} color="#1890ff" />
                </div>
                <span className="metric-value">{campaignData.totalQueued || 0}</span>
              </div>
              <div className="metric-description">Total emails queued</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Sent</span>
                  <CheckCircle2 size={18} color="#52c41a" />
                </div>
                <span className="metric-value">{campaignData.totalMailSent || 0}</span>
              </div>
              <div className="metric-description">Emails sent successfully</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Opened</span>
                  <Eye size={18} color="#722ed1" />
                </div>
                <span className="metric-value">{campaignData.totalOpened || 0}</span>
              </div>
              <div className="metric-description">Emails opened</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Clicked</span>
                  <ArrowUpRight size={18} color="#eb2f96" />
                </div>
                <span className="metric-value">{campaignData.totalClicked || 0}</span>
              </div>
              <div className="metric-description">Link clicks</div>
            </Card>
          </>
        ) : (
          // WhatsApp Campaign Metrics
          <>
            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Sent</span>
                  <Send size={18} color="#1890ff" />
                </div>
                <span className="metric-value">{campaignData.totalSent}</span>
              </div>
              <div className="metric-description">Total messages sent</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Delivered</span>
                  <CheckCircle2 size={18} color="#52c41a" />
                </div>
                <span className="metric-value">{campaignData.totalDelivered}</span>
              </div>
              <div className="metric-description">Successfully delivered</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Read</span>
                  <Eye size={18} color="#722ed1" />
                </div>
                <span className="metric-value">{campaignData.totalRead}</span>
              </div>
              <div className="metric-description">Messages read</div>
            </Card>

            <Card className="metric-card">
              <div className="metric-header">
                <div className="metric-title">
                  <span>Replied</span>
                  <MessageSquareReply size={18} color="#eb2f96" />
                </div>
                <span className="metric-value">{campaignData.totalReplied}</span>
              </div>
              <div className="metric-description">Responses received</div>
            </Card>
          </>
        )}
      </div>

      <div className="metrics-grid-secondary">
  {isEmailService(campaignData.serviceType) ? (
    // Email Campaign Secondary Metrics
    <>
      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Processed</span>
            <AlertCircle size={18} color="#faad14" />
          </div>
          <span className="metric-value">{campaignData.totalProcessed || 0}</span>
        </div>
        <div className="metric-description">Emails processed</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Open Rate</div>
          <span className="metric-value">{campaignData.openRate || 0}%</span>
        </div>
        <div className="metric-description">Email open percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Click Rate</div>
          <span className="metric-value">{campaignData.clickRate || 0}%</span>
        </div>
        <div className="metric-description">Link click percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Daily Limit</span>
            <Calendar size={18} color="#1890ff" />
          </div>
          <span className="metric-value">{campaignData.dailySentCount || 0}/{campaignData.dailyLimit || 0}</span>
        </div>
        <div className="metric-description">Daily sending progress</div>
      </Card>
    </>
  ) : (
    // WhatsApp Campaign Secondary Metrics
    <>
      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">
            <span>Failed</span>
            <AlertCircle size={18} color="#f5222d" />
          </div>
          <span className="metric-value">{campaignData.totalFailed}</span>
        </div>
        <div className="metric-description">Failed deliveries</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Delivery Rate</div>
          <span className="metric-value">{campaignData.totalDeliveredRate}%</span>
        </div>
        <div className="metric-description">Successful delivery percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Read Rate</div>
          <span className="metric-value">{campaignData.totalReadRate}%</span>
        </div>
        <div className="metric-description">Message read percentage</div>
      </Card>

      <Card className="metric-card">
        <div className="metric-header">
          <div className="metric-title">Reply Rate</div>
          <span className="metric-value">{campaignData.totalRepliedRate}%</span>
        </div>
        <div className="metric-description">Message reply percentage</div>
      </Card>
    </>
  )}
</div>

      <Card>
        <div className="leads-table-header">
        <h2>Campaign Leads ({campaignData.totalLeads})</h2>
          <div className="leads-controls">
            <Select
              className="status-select"
              placeholder="Filter Status"
              style={{ width: 160 }} 
              value={selectedStatus}
              onChange={handleStatusChange}
              options={[
                { value: '', label: 'All Status' },
                { value: 'sent', label: 'Sent' },
                { value: 'delivered', label: 'Delivered' },
                { value: 'read', label: 'Read' },
                { value: 'replied', label: 'Replied' },
                { value: 'failed', label: 'Failed' }
               
              ]}
            />
            
            <div className="search-box">
  <Search size={16} />
  <input
    type="text"
    placeholder="Search leads..."
    value={inputValue}  // Use inputValue instead of searchTerm
    onChange={handleInputChange}
  />
  {inputValue && (  // Show X when there's input
    <X 
      size={16}
      className="cursor-pointer"
      onClick={handleClearSearch}
    />
  )}
</div>
<button 
      className="export-button"
      onClick={handleExportModalToggle}
    >
      <Download size={16} />
      Export
    </button>
    <GenericConfirmationModal 
  showModal={isExportModalOpen} 
  toggleModal={handleExportModalToggle} 
  onConfirmation={confirmExport}
  title="Confirm Export" 
  message={`Export ${campaignData?.totalLeads || 0} leads?
    ${selectedStatus ? `Filtered by status: ${selectedStatus}` : ''}
    ${searchTerm ? `Searching for: "${searchTerm}"` : ''}`}
  confirmText="Export"
  cancelText="Cancel"
  isWarning={false}
/>
  
          </div>
        </div>

        {selectedLeads.length > 0 && (
  <div className="mb-3 d-flex justify-content-end">
    <button 
      className="btn"
      onClick={handleCreateCampaign}
      style={{
        backgroundColor: '#1677FF',
        color: 'white',
        borderRadius: '6px',
        padding: '8px 16px',
        fontWeight: '500',
        boxShadow: '0 2px 0 rgba(0,0,0,0.045)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}
    >
      <MessageSquare size={16} />
      Create Campaign with {selectedLeads.length} Leads
    </button>
  </div>
)}

        <Table
          loading={loading}
          dataSource={campaignData.leads}
          columns={[
            {
              title: () => (
                <Checkbox 
                  checked={allLeadsSelected || (campaignData?.leads && selectedLeads.length === campaignData.leads.length && selectedLeads.length > 0)}
                  onChange={(e) => handleSelectAllLeads(e.target.checked)}
                />
              ),
              key: 'selection',
              width: '50px',
              render: (_, record) => (
                <Checkbox
                  checked={selectedLeads.includes(record._id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLeads(prev => [...prev, record._id]);
                    } else {
                      setSelectedLeads(prev => prev.filter(id => id !== record._id));
                      setAllLeadsSelected(false);
                    }
                  }}
                />
              )
            },
            {
              title: 'Lead Name',
              dataIndex: 'name',
              key: 'name',
              render: (name: string) => name || 'N/A'
            },
            {
              title: 'Status',
              dataIndex: 'messageStatus',
              key: 'messageStatus',
              render: (status: StatusType) => {
                const colorMap: { [key: string]: string } = {
                  sent: '#1890ff',      // Blue
                  delivered: '#52c41a',  // Green
                  read: '#722ed1',      // Purple
                  replied: '#eb2f96',   // Pink
                  failed: '#f5222d' 
                };
            
                const color = colorMap[status?.toLowerCase()] || '#8c8c8c';
                
                return (
                  <span className="status-badge" style={{ backgroundColor: color }}>
                    {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
                  </span>
                );
              }
            },
            {
              title: 'Last Activity',
              key: 'lastActivity',
              render: (_, record) => record.lastActivityLabel || 'No activity',
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => <ActionButtons lead={record} />,
            },
          ]}
          pagination={{
            total: campaignData.totalLeads,
            pageSize: pageSize,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`,
            position: ['bottomCenter']
          }}
          scroll={{ y: 400 }}
          className="leads-table"
        />
      </Card>
      {selectedLead && (
  <DrawerComponent
    openWithHeader={openWithHeader}
    setOpenWithHeader={setOpenWithHeader}
    customHeader={false}
    drawerTitle="Lead Details"
    size="lg"
    className="lead_details"
  >
   <LeadDetails
  _id={selectedLead._id}
  name={selectedLead.name}
  integration={selectedLead.integration}
  customSource={selectedLead.customSource}
  createdAt={selectedLead.createdAt}
  status={selectedLead.status}
  saleValue={selectedLead.saleValue}
  website={selectedLead.website}
  label={selectedLead.label}
  // ... other required props
  deleteLeadFromDrawer={() => {}} // Provide default no-op functions for callback props
  copyLeadToList={() => {}}
  moveLeadToList={() => {}}
  onLabelClick={() => {}}
  onStatusClick={() => {}}
  setFrom={() => {}}
  handleEditModal={() => {}}
  handleLeadDetailsModalOpen={() => {}}
  onLeadFileUpload={() => {}}
  editLead={() => {}}
  leadFileDelete={() => {}}
/>
  </DrawerComponent>
)}
      </div>
  </div>
);
};
export default CampaignView;